import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';
import { useTokenConfig } from '../contexts/TokenConfigContext';
import { assetKey } from '../utils/assetIdentity';
import { Repeat, Play, Square, Search, RotateCcw, CheckCircle2, XCircle, Clock, Loader2, Users, ExternalLink } from 'lucide-react';

const API_URL = getApiUrl();



const tonOf = (w) => Number(w.ton_balance || 0) / 1e9;
const short = (a) => (a ? a.slice(0, 6) + '…' + a.slice(-4) : '');
const fmt = (n, d = 2) => (n == null || isNaN(n) ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: d }));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const DELIVERED = ['delivered', 'success'];
const FAILEDST = ['failed', 'uncertain'];
const stMeta = (s) =>
  DELIVERED.includes(s) ? { Icon: CheckCircle2, cls: 'text-green-600' }
  : FAILEDST.includes(s) ? { Icon: XCircle, cls: 'text-red-600' }
  : s === 'running' ? { Icon: Loader2, cls: 'text-blue-600 animate-spin' }
  : { Icon: Clock, cls: 'text-amber-500' };

const LEVEL_CLS = {
  info: 'text-slate-300', ok: 'text-green-400', warn: 'text-amber-400',
  error: 'text-red-400', step: 'text-sky-400 font-semibold', done: 'text-fuchsia-400 font-semibold',
};

const CycleBootstrap = () => {
  const { tokenAddress, tokenSymbol } = useTokenConfig();
  const pubgOf = (w) => {
    const value = Object.entries(w.jetton_balances || {}).find(([key]) => assetKey(key) === assetKey(tokenAddress))?.[1];
    return Number(typeof value === 'object' ? value?.balance || 0 : value || 0) / 1e9;
  };
  const [source, setSource] = useState('');
  const [chunkUsd, setChunkUsd] = useState(100);
  const [chunkTon, setChunkTon] = useState(62.5);
  const [tonMin, setTonMin] = useState(8);     // рандом «от» TON на воркера
  const [tonMax, setTonMax] = useState(12);    // рандом «до» TON на воркера
  const [hops, setHops] = useState(5);
  const [maxWorkers, setMaxWorkers] = useState(3);
  const [pubgMinM, setPubgMinM] = useState(0.1);
  const [pubgMaxM, setPubgMaxM] = useState(5);
  const [mode, setMode] = useState('linear');        // 'linear' | 'split_aggregate'
  const [flMin, setFlMin] = useState(5);             // кошельков первой линии, от
  const [flMax, setFlMax] = useState(10);            // кошельков первой линии, до
  const [twoLegProb, setTwoLegProb] = useState(0.4); // вероятность 2 переводов на получателя
  const [saSeed, setSaSeed] = useState(null);        // сид плана из dry-run → в бой (превью == прогон)

  const [prices, setPrices] = useState({ pubg: 0, ton: 0 });
  const [srcInfo, setSrcInfo] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [running, setRunning] = useState(false);
  const [runId, setRunId] = useState(null);
  const [undelivered, setUndelivered] = useState(0);
  const [verify, setVerify] = useState({});
  const [plan, setPlan] = useState({});   // address -> запланированная случайная сумма (превью)
  const [log, setLog] = useState([]);
  const cancelRef = useRef(false);
  const loggedRef = useRef(new Set());
  const logEndRef = useRef(null);

  const addLog = useCallback((msg, level = 'info') => {
    const t = new Date().toLocaleTimeString('ru-RU', { hour12: false });
    setLog((l) => [...l, { t, msg, level }]);
  }, []);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [log]);
  useEffect(() => {
    axios.get(`${API_URL}/api/market-maker/current-token-price`)
      .then((r) => { if (r.data?.success) setPrices({ pubg: r.data.price_usd || 0, ton: r.data.ton_price_usd || 0 }); })
      .catch(() => {});
  }, []);
  useEffect(() => { const n = Number(chunkUsd); if (n) setChunkTon(+(n / (prices.ton || 1.6)).toFixed(2)); /* eslint-disable-next-line */ }, [prices.ton]);
  const onChunkUsd = (v) => { setChunkUsd(v); const n = Number(v); setChunkTon(n ? +(n / (prices.ton || 1.6)).toFixed(2) : ''); };
  const onChunkTon = (v) => { setChunkTon(v); const n = Number(v); setChunkUsd(n ? +(n * (prices.ton || 1.6)).toFixed(2) : ''); };

  // ---- live plan (random range) ----
  const plannedCount = workers.length || Number(maxWorkers) || 0;
  const avgTon = (Number(tonMin) + Number(tonMax)) / 2;
  const totalApprox = plannedCount * avgTon;
  const needTon = plannedCount * Number(tonMax) + plannedCount * 0.3 + 2; // worst-case
  const targetUsd = needTon * prices.ton * 1.06;
  const numChunks = Math.max(1, Math.ceil(targetUsd / Math.max(1, Number(chunkUsd))));

  const fetchWallets = async () => (await axios.get(`${API_URL}/api/wallets/list`)).data?.wallets || [];
  const pickWorkers = (all) => {
    const lo = pubgMinM * 1e6, hi = pubgMaxM * 1e6;
    return all
      .filter((w) => w.address !== source && !['BUYER-MM', 'BIGBUY'].includes(w.group_name))
      .map((w) => ({ address: w.address, pubg: pubgOf(w), ton: tonOf(w), group: w.group_name }))
      .filter((w) => w.pubg >= lo && w.pubg < hi && w.ton < 0.5)
      .sort((a, b) => b.pubg - a.pubg)
      .slice(0, Number(maxWorkers));
  };
  const rnd = (a, b) => +(Number(a) + Math.random() * (Number(b) - Number(a))).toFixed(2);
  const genPlan = (picked) => { const p = {}; picked.forEach((w) => { p[w.address] = rnd(tonMin, tonMax); }); return p; };
  const genPlanRange = (picked, lo, hi) => { const p = {}; picked.forEach((w) => { p[w.address] = rnd(lo, hi); }); return p; };
  // авто-предложение разумных параметров (без заоблачных значений): диапазон ±20% вокруг середины + 3–5 хопов
  const suggest = () => {
    const mid = (Number(tonMin) + Number(tonMax)) / 2 || 10;
    const lo = Math.max(0.5, +(mid * 0.8).toFixed(1)), hi = +(mid * 1.2).toFixed(1);
    const h = 3 + Math.floor(Math.random() * 3); // 3..5 (бэкенд макс 5)
    setTonMin(lo); setTonMax(hi); setHops(h);
    toast.success(`Предложено: ${lo}–${hi} TON на воркера, ${h} транзит-хопов`);
  };

  const dryCheck = async () => {
    setLog([]); addLog('Проверка (без денег)…', 'step');
    try {
      const all = await fetchWallets();
      const src = all.find((w) => w.address === source);
      if (!src) { addLog('✖ Источник не найден', 'error'); return; }
      const si = { ton: tonOf(src), pubg: pubgOf(src), group: src.group_name };
      setSrcInfo(si);
      addLog(`Источник [${si.group}]: TON=${fmt(si.ton, 3)} · ${tokenSymbol}=${fmt(si.pubg, 0)}`, 'info');
      if (si.ton < 1.5) addLog(`⚠ Газа ${fmt(si.ton, 2)} TON — свап-продаже нужно ~1.5+`, 'warn');
      const picked = pickWorkers(all);
      setWorkers(picked);
      const dp = genPlan(picked); setPlan(dp);
      setRecipients(picked.map((w) => ({ address: w.address, amount: null, status: 'pending' })));
      if (!picked.length) { addLog('✖ Воркеры по фильтру не найдены', 'error'); return; }
      if (mode === 'split_aggregate') {
        try {
          const resp = await axios.post(`${API_URL}/api/wallets/distribute-split-aggregate`, {
            from_address: source, recipients: picked.map((w) => w.address),
            amount_min_ton: Number(tonMin), amount_max_ton: Number(tonMax),
            two_leg_probability: Number(twoLegProb), first_line_min: Number(flMin), first_line_max: Number(flMax),
            dry_run: true,
          });
          const pv = resp.data?.plan_preview;
          if (!pv) { addLog('✖ План не построен: ' + (resp.data?.error || 'мало средств на источнике?'), 'error'); return; }
          setSaSeed(pv.seed);   // тот же план исполнится в бою
          setPlan(Object.fromEntries(pv.per_recipient.map((r) => [r.address, r.target_ton])));
          setRecipients(pv.per_recipient.map((r) => ({ address: r.address, amount: r.target_ton, status: 'pending' })));
          addLog(`✓ План: ${pv.M} кош. первой линии · всего ${fmt(pv.send_total_ton, 2)} TON (газ ${fmt(pv.gas_pool_ton, 2)}) · диапазон ${pv.range_ton[0]}–${pv.range_ton[1]} TON`, 'ok');
          pv.per_recipient.forEach((r) => addLog(`  ${short(r.address)} = ${fmt(r.target_ton, 2)} TON [${r.legs.map((l) => 'FL' + l.first_line + ':' + fmt(l.slice_ton, 2)).join(' + ')}]`, 'info'));
          addLog('Готово. Жми «Запустить цикл».', 'ok');
        } catch (e) {
          addLog('✖ Эндпоинт split-aggregate недоступен (нужен рестарт бэкенда): ' + (e.response?.data?.detail || e.message), 'error');
        }
        return;
      }
      const tt = picked.length * avgTon;
      const tu = (picked.length * Number(tonMax) + picked.length * 0.3 + 2) * prices.ton * 1.06;
      const nc = Math.max(1, Math.ceil(tu / Math.max(1, Number(chunkUsd))));
      addLog(`✓ Раздать случайно ${tonMin}–${tonMax} TON на ${picked.length} кош. ≈ ${fmt(tt, 0)} TON (макс ${fmt(picked.length * Number(tonMax), 0)})`, 'ok');
      addLog(`  план сумм (пример): ${picked.slice(0, 5).map((w) => fmt(dp[w.address], 1)).join(', ')}${picked.length > 5 ? '…' : ''} TON`, 'info');
      addLog(`Продать ~$${fmt(tu, 0)} ${tokenSymbol} чанками по $${chunkUsd} → ${nc} чанк(ов)`, 'info');
      addLog('Готово. Жми «Запустить цикл».', 'ok');
    } catch (e) { addLog('✖ ' + (e.response?.data?.detail || e.message), 'error'); }
  };

  const pollJob = async (tid) => {
    let guard = 0;
    while (!cancelRef.current && guard++ < 800) {
      await sleep(1500);
      try {
        const jb = (await axios.get(`${API_URL}/api/wallets/distribute/jobs/${tid}`)).data?.job;
        const recs = jb?.recipients || [];
        if (recs.length) setRecipients(recs);
        for (const r of recs) {
          if (DELIVERED.includes(r.status) && !loggedRef.current.has(r.address)) {
            loggedRef.current.add(r.address);
            addLog(`  ✓ ${short(r.address)} — отправлено ${fmt(r.amount, 2)} TON`, 'ok');
          } else if (FAILEDST.includes(r.status) && !loggedRef.current.has('F' + r.address)) {
            loggedRef.current.add('F' + r.address);
            addLog(`  ✖ ${short(r.address)} — ${r.status}`, 'error');
          }
        }
        const resolved = recs.filter((r) => DELIVERED.includes(r.status) || FAILEDST.includes(r.status)).length;
        if (recs.length && resolved >= recs.length) {
          const del = recs.filter((r) => DELIVERED.includes(r.status)).length;
          const und = recs.length - del;
          setUndelivered(und);
          addLog(`■ Отправлено ${del}/${recs.length}${und ? ` · не доставлено ${und} → «Resume»` : ''}`, 'done');
          return;
        }
      } catch (_) { /* job ещё не персистнулся */ }
    }
  };

  // независимая on-chain сверка: сравниваем с ФАКТИЧЕСКОЙ (случайной) суммой, назначенной каждому
  const verifyOnChain = async (picked, tid, floorMin) => {
    addLog('Сверяю on-chain: обновляю реальные балансы получателей…', 'step');
    try {
      const expected = {};
      try {
        const jb = (await axios.get(`${API_URL}/api/wallets/distribute/jobs/${tid}`)).data?.job;
        (jb?.recipients || []).forEach((r) => { if (r.amount != null) expected[r.address] = Number(r.amount); });
      } catch (_) {}
      await axios.post(`${API_URL}/api/wallets/update-balances`, { addresses: picked.map((w) => w.address) });
      const all = await fetchWallets();
      const vmap = {}; let okc = 0;
      for (const w of picked) {
        const cur = all.find((a) => a.address === w.address);
        const post = cur ? tonOf(cur) : w.ton;
        const delta = post - w.ton;
        const exp = expected[w.address] ?? Number(floorMin);
        const ok = delta >= exp * 0.9;
        vmap[w.address] = { pre: w.ton, post, delta, ok };
        if (ok) okc++;
        addLog(`  ${ok ? '✓' : '⚠'} ${short(w.address)}: ${fmt(w.ton, 2)} → ${fmt(post, 2)} TON (${delta >= 0 ? '+' : ''}${fmt(delta, 2)}, ждали ~${fmt(exp, 2)})${ok ? ' — подтверждено' : ' — НА БАЛАНСЕ НЕ ВИДНО!'}`, ok ? 'ok' : 'error');
      }
      setVerify(vmap);
      addLog(`■ On-chain подтверждено: ${okc}/${picked.length}`, okc === picked.length ? 'done' : 'warn');
    } catch (e) {
      addLog('⚠ Не смог сверить балансы on-chain: ' + (e.response?.data?.detail || e.message), 'warn');
    }
  };

  // Split→Aggregate: поток прогресса из distribute_runs (иная форма, чем pollJob)
  const iterLine = (it) => Object.entries(it)
    .filter(([k]) => !['step', 'kind', 'status', 'ts'].includes(k))
    .map(([k, v]) => `${k}=${v}`).join(' ');
  const pollRun = async (rid) => {
    let lastStep = 0, done = false, guard = 0;
    while (!cancelRef.current && !done && guard++ < 1200) {
      await sleep(1500);
      let doc;
      try { doc = (await axios.get(`${API_URL}/api/wallets/distribute-runs/${rid}`)).data?.run; } catch (_) { continue; }
      if (!doc) continue;
      const pv = doc.plan_preview;
      if (pv?.per_recipient?.length && !doc.checkup) {
        setPlan(Object.fromEntries(pv.per_recipient.map((r) => [r.address, r.target_ton])));
        setRecipients(pv.per_recipient.map((r) => ({ address: r.address, amount: r.target_ton, status: 'running' })));
      }
      for (const it of (doc.iterations || [])) {
        if (it.step > lastStep) {
          lastStep = it.step;
          const lvl = it.status === 'failed' ? 'error'
            : (it.kind === 'checkup' || it.kind === 'plan') ? 'step'
            : ['ok', 'ready', 'sent'].includes(it.status) ? 'ok' : 'info';
          addLog(`  [${it.kind}] ${it.status} ${iterLine(it)}`, lvl);
        }
      }
      if (['completed', 'completed_with_mismatch', 'failed', 'error'].includes(doc.status)) {
        done = true;
        const chk = doc.checkup;
        if (chk?.per_recipient) {
          const vm = {};
          setRecipients(chk.per_recipient.map((r) => {
            vm[r.address] = { pre: r.before_ton, post: r.after_ton, delta: r.delta_ton, ok: r.ok };
            return { address: r.address, amount: r.target_ton, status: r.ok ? 'delivered' : 'failed' };
          }));
          setVerify(vm);
          addLog(`■ Доставлено ${chk.delivered_recipients}/${chk.requested_recipients} · потрачено ${doc.summary?.net_spent_ton} TON`, chk.all_ok ? 'done' : 'warn');
        } else {
          addLog(`■ Статус: ${doc.status}${doc.error ? ' — ' + doc.error : ''}`, doc.status === 'completed' ? 'done' : 'error');
        }
        if (doc.stranded?.length) {
          addLog(`⚠ ЗАСТРЯЛО на ${doc.stranded.length} кош. первой линии — вернуть вручную (средства НЕ потеряны, на наших адресах):`, 'error');
          doc.stranded.forEach((s) => addLog(`   ${short(s.address)}: ${fmt(s.residual_ton, 4)} TON`, 'error'));
        }
      }
    }
  };

  const runCycle = async () => {
    if (!source || prices.pubg <= 0 || prices.ton <= 0) { toast.error('Укажите источник и дождитесь актуальных цен'); return; }
    setRunning(true); cancelRef.current = false; loggedRef.current = new Set(); setLog([]); setUndelivered(0); setVerify({});
    const stop = () => cancelRef.current;
    try {
      addLog('▶ Старт цикла', 'step');
      addLog('1/5 · Отбор воркеров и проверка источника…', 'step');
      const all = await fetchWallets();
      const src = all.find((w) => w.address === source);
      if (!src) { addLog('✖ Источник не найден', 'error'); return; }
      const si = { ton: tonOf(src), pubg: pubgOf(src), group: src.group_name };
      setSrcInfo(si);
      addLog(`Источник [${si.group}]: TON=${fmt(si.ton, 3)} · ${tokenSymbol}=${fmt(si.pubg, 0)}`, 'info');
      if (si.ton < 1.5) { addLog(`✖ Мало газа: ${fmt(si.ton, 2)} TON < 1.5 — продажа не пройдёт.`, 'error'); return; }
      const picked = pickWorkers(all);
      if (!picked.length) { addLog('✖ Воркеры не найдены', 'error'); return; }
      setWorkers(picked);
      setPlan(genPlan(picked));
      setRecipients(picked.map((w) => ({ address: w.address, amount: null, status: 'pending' })));
      const nChunks = Math.max(1, Math.ceil(((picked.length * Number(tonMax) + picked.length * 0.3 + 2) * prices.ton * 1.06) / Math.max(1, Number(chunkUsd))));
      addLog(`✓ План: раздать случайно ${tonMin}–${tonMax} TON на ${picked.length} кош. (макс ${fmt(picked.length * Number(tonMax), 0)} TON) · продать в ${nChunks} чанк(ов)`, 'ok');
      if (stop()) return addLog('⏹ Остановлено', 'warn');

      // 2) chunked sale
      const srcPubgUsd = Math.max(1, si.pubg * prices.pubg);
      let chunkPct = Math.min(Math.max((Number(chunkUsd) / srcPubgUsd) * 100, 0.05), 50);
      addLog(`2/5 · Продажа ${nChunks} чанк(ов) по ~$${chunkUsd} (${chunkPct.toFixed(2)}% каждый)…`, 'step');
      let soldChunks = 0;
      for (let k = 1; k <= nChunks && !stop(); k++) {
        addLog(`  чанк ${k}/${nChunks}: продаю ${chunkPct.toFixed(2)}% ${tokenSymbol}…`, 'info');
        const sr = await axios.post(`${API_URL}/api/wallets/mass-sell`, {
          addresses: [source], token_address: tokenAddress, min_percent: chunkPct, max_percent: chunkPct, slippage: 5, background: false,
        });
        const succ = sr.data?.results?.success || [];
        const fail = sr.data?.results?.failed || [];
        if (!succ.length) { addLog(`  ✖ чанк ${k}: ${fail[0]?.error || 'unknown'}`, 'error'); if (soldChunks === 0) return; addLog('  продолжаю с поднятым', 'warn'); break; }
        soldChunks++;
        addLog(`  ✓ чанк ${k}/${nChunks} продан`, 'ok');
        if (k < nChunks) for (let s = 0; s < 6 && !stop(); s++) await sleep(1000);
      }
      if (stop()) return addLog('⏹ Остановлено', 'warn');

      // 3) settle + read real source balance
      addLog('3/5 · Жду расчёта on-chain (15с) и читаю баланс источника…', 'info');
      for (let i = 0; i < 15 && !stop(); i++) await sleep(1000);
      if (stop()) return addLog('⏹ Остановлено', 'warn');
      let S = si.ton;
      try {
        await axios.post(`${API_URL}/api/wallets/update-balances`, { addresses: [source] });
        const all2 = await fetchWallets();
        const now = all2.find((w) => w.address === source);
        if (now) S = tonOf(now);
      } catch (_) {}
      addLog(`Баланс источника после продажи: ${fmt(S, 2)} TON`, 'info');

      // ── режим Split→Aggregate: показать РЕАЛЬНЫЙ план (на баланс после продажи) и исполнить ровно его ──
      if (mode === 'split_aggregate') {
        addLog('4/4 · Split→Aggregate: строю план на баланс ПОСЛЕ продажи…', 'step');
        const saBody = {
          from_address: source, recipients: picked.map((w) => w.address),
          amount_min_ton: Number(tonMin), amount_max_ton: Number(tonMax),
          two_leg_probability: Number(twoLegProb), first_line_min: Number(flMin), first_line_max: Number(flMax),
        };
        let pv;
        try {
          pv = (await axios.post(`${API_URL}/api/wallets/distribute-split-aggregate`, { ...saBody, dry_run: true })).data?.plan_preview;
        } catch (e) {
          addLog('✖ Эндпоинт недоступен (нужен рестарт бэкенда?): ' + (e.response?.data?.detail || e.message), 'error'); return;
        }
        if (!pv) { addLog('✖ План не построен (мало TON после продажи?)', 'error'); return; }
        addLog(`План: ${pv.M} кош. первой линии · всего ${fmt(pv.send_total_ton, 2)} TON · диапазон ${pv.range_ton[0]}–${pv.range_ton[1]} TON`, 'ok');
        pv.per_recipient.forEach((r) => addLog(`  ${short(r.address)} = ${fmt(r.target_ton, 2)} TON [${r.legs.map((l) => 'FL' + l.first_line + ':' + fmt(l.slice_ton, 2)).join(' + ')}]`, 'info'));
        setPlan(Object.fromEntries(pv.per_recipient.map((r) => [r.address, r.target_ton])));
        setRecipients(pv.per_recipient.map((r) => ({ address: r.address, amount: r.target_ton, status: 'pending' })));
        if (stop()) return addLog('⏹ Остановлено до отправки', 'warn');
        // исполняем РОВНО показанный план: тот же seed + тот же баланс → идентичный результат
        let resp;
        try {
          resp = await axios.post(`${API_URL}/api/wallets/distribute-split-aggregate`, { ...saBody, seed: pv.seed });
        } catch (e) {
          addLog('✖ Не стартовало: ' + (e.response?.data?.detail || e.message), 'error'); return;
        }
        const rid = resp.data?.run_id;
        if (!rid) { addLog('✖ Не стартовало: ' + (resp.data?.error || JSON.stringify(resp.data)), 'error'); return; }
        setRunId(rid);
        addLog(`✓ Запущено (run ${short(rid)}). Исполняю показанный план…`, 'ok');
        await pollRun(rid);
        return;
      }

      // 4) random distribution via transit (percentage mode → random per wallet in [tonMin,tonMax])
      // БЮДЖЕТ-СЕЙФ: оставляем резерв на источнике (газ источника + стоимость хопов),
      // и подгоняем верх диапазона так, чтобы picked.length × макс ≤ бюджет.
      // → даже если КАЖДЫЙ возьмёт максимум, на всех хватит; перерасхода и «пустых» адресов не будет.
      const hopN = Math.min(5, Math.max(1, Number(hops)));
      const reserve = 2 + picked.length * hopN * 0.06;
      const budget = Math.max(0, S - reserve);
      if (budget < picked.length * 0.5) {
        addLog(`✖ Доступно после резерва ${fmt(budget, 1)} TON — мало даже на минимум (${picked.length}×0.5). Продай больше или уменьши воркеров.`, 'error'); return;
      }
      let loTon = Math.max(0.3, Number(tonMin)), hiTon = Number(tonMax);
      const maxHiFit = budget / picked.length;             // верх, при котором на ВСЕХ хватит
      if (hiTon > maxHiFit) {
        hiTon = +maxHiFit.toFixed(2);
        if (loTon > hiTon) loTon = +(hiTon * 0.7).toFixed(2);
        addLog(`⚠ Бюджет ${fmt(budget, 1)} TON: урезаю диапазон до ${fmt(loTon, 2)}–${fmt(hiTon, 2)} TON, чтобы хватило всем ${picked.length}`, 'warn');
      }
      setPlan(genPlanRange(picked, loTon, hiTon));           // превью = фактический подогнанный диапазон
      let minPct = (loTon / S) * 100;
      let maxPct = (hiTon / S) * 100;
      minPct = Math.max(0.01, Math.min(minPct, maxPct));
      addLog(`4/5 · Раздаю СЛУЧАЙНО ${fmt(loTon, 2)}–${fmt(hiTon, 2)} TON на ${picked.length} кош. через ${hopN} хопов · макс всего ${fmt(picked.length * hiTon, 1)} ≤ бюджет ${fmt(budget, 1)} TON (резерв ${fmt(reserve, 1)})…`, 'step');
      const mnemonic = (await axios.get(`${API_URL}/api/wallets/wallet/${source}/mnemonic`)).data?.mnemonic;
      if (!mnemonic) { addLog('✖ Нет мнемоники источника', 'error'); return; }
      const dr = await axios.post(`${API_URL}/api/wallets/distribute-ton`, {
        from_address: source, from_mnemonic: mnemonic, to_addresses: picked.map((w) => w.address),
        amount_type: 'percentage', min_percent: +minPct.toFixed(3), max_percent: +maxPct.toFixed(3),
        intermediate_hops: Math.min(5, Math.max(1, Number(hops))), delay_seconds: 1.0, background: true,
      });
      const tid = dr.data?.task_id;
      if (!tid) { addLog('✖ Раздача не стартовала', 'error'); return; }
      setRunId(tid);
      addLog(`✓ Раздача запущена (run ${short(tid)}). Проверяю доставку по-адресно…`, 'ok');
      await pollJob(tid);

      // 5) independent on-chain verify (floor = tonMin)
      if (!stop()) { addLog('5/5 · Независимая on-chain сверка…', 'step'); for (let s = 0; s < 5 && !stop(); s++) await sleep(1000); await verifyOnChain(picked, tid, Number(tonMin)); }
      if (stop()) addLog('⏹ Остановлено (отправленное не откатывается)', 'warn');
    } catch (e) {
      addLog('✖ Сбой: ' + (e.response?.data?.detail || e.message), 'error');
    } finally { setRunning(false); }
  };

  const runResume = async () => {
    if (!runId) return;
    setRunning(true); cancelRef.current = false; loggedRef.current = new Set();
    try {
      addLog(`↻ Resume недоставленных (run ${short(runId)})…`, 'step');
      const r = await axios.post(`${API_URL}/api/wallets/distribute/resume/${runId}`);
      const tid = r.data?.task_id;
      addLog(`✓ Resume запущен (было не доставлено ${r.data?.undelivered ?? '?'}), новый run ${short(tid)}`, 'ok');
      setRunId(tid); setUndelivered(0);
      await pollJob(tid);
      if (!cancelRef.current) await verifyOnChain(workers, tid, Number(tonMin));
    } catch (e) { addLog('✖ Resume: ' + (e.response?.data?.detail || e.message), 'error'); }
    finally { setRunning(false); }
  };

  const stopCycle = () => { cancelRef.current = true; toast('Останавливаю после текущего шага…'); };

  const rows = recipients.length ? recipients : workers.map((w) => ({ address: w.address, amount: null, status: 'pending' }));
  const delivered = rows.filter((r) => DELIVERED.includes(r.status)).length;

  const Field = ({ label, value, set }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <Input type="number" value={value} onChange={(e) => set(e.target.value)} disabled={running} />
    </div>
  );

  return (
    <div className="space-y-4 text-gray-900">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
          <Repeat className="h-6 w-6 text-indigo-600" /> Cycle Bootstrap
        </h1>
        <p className="text-sm text-gray-500">
          Одна кнопка: продать {tokenSymbol} чанками → прогнать TON через транзитные хопы → зафандить наши кошельки {tokenSymbol} СЛУЧАЙНЫМИ суммами. С проверками и живым логом.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-900">Параметры</CardTitle>
          <CardDescription>Пилот = 3 воркера. Полный прогон — подними «Макс. воркеров» до 60.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Источник — кошелёк {tokenSymbol} с газом ≥1.5 TON</label>
            <Input value={source} onChange={(e) => setSource(e.target.value)} disabled={running} className="font-mono text-xs" />
            {srcInfo && <div className="text-xs text-gray-500 mt-1">[{srcInfo.group}] TON={fmt(srcInfo.ton, 3)} · {tokenSymbol}={fmt(srcInfo.pubg, 0)}</div>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Чанк продажи — вводи в $ ИЛИ TON (пересчитывается сразу)</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <Input type="number" className="pl-6" value={chunkUsd} onChange={(e) => onChunkUsd(e.target.value)} disabled={running} />
              </div>
              <span className="text-gray-400 select-none">⇄</span>
              <div className="relative flex-1">
                <Input type="number" className="pr-11" value={chunkTon} onChange={(e) => onChunkTon(e.target.value)} disabled={running} />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">TON</span>
              </div>
            </div>
            <div className="text-xs text-gray-400 mt-1">курс: 1 TON = ${fmt(prices.ton, 2)}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Топология раздачи</label>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setMode('linear')} disabled={running} className={mode === 'linear' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : ''}>Линейный транзит</Button>
              <Button size="sm" variant="outline" onClick={() => setMode('split_aggregate')} disabled={running} className={mode === 'split_aggregate' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : ''}>Split→Aggregate (веер + сбор)</Button>
            </div>
          </div>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-6">
            <Field label="TON на воркера, от" value={tonMin} set={setTonMin} />
            <Field label="TON на воркера, до" value={tonMax} set={setTonMax} />
            <Field label="Транзит-хопов (1–5)" value={hops} set={setHops} />
            <Field label="Макс. воркеров" value={maxWorkers} set={setMaxWorkers} />
            <Field label={`${tokenSymbol} от (млн)`} value={pubgMinM} set={setPubgMinM} />
            <Field label={`${tokenSymbol} до (млн)`} value={pubgMaxM} set={setPubgMaxM} />
          </div>
          {mode === 'split_aggregate' && (
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3">
              <Field label="Кош. первой линии, от" value={flMin} set={setFlMin} />
              <Field label="Кош. первой линии, до" value={flMax} set={setFlMax} />
              <Field label="Вероятн. 2 переводов (0–1)" value={twoLegProb} set={setTwoLegProb} />
            </div>
          )}

          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900 flex items-start gap-2">
            <Users className="h-5 w-5 mt-0.5 text-indigo-600 shrink-0" />
            <div>
              Раздать <b>случайно {fmt(tonMin, 1)}–{fmt(tonMax, 1)} TON</b> на <b>{plannedCount} {workers.length ? 'кошельков (отобрано)' : 'кошельков (макс.)'}</b> ≈ <b>{fmt(totalApprox, 0)} TON</b> (макс {fmt(plannedCount * Number(tonMax), 0)}) через <b>{mode === 'split_aggregate' ? `${flMin}–${flMax} кош. первой линии` : `${hops} хопов`}</b>.
              <span className="text-indigo-700"> Продать ~<b>${fmt(targetUsd, 0)}</b> {tokenSymbol} чанками по ${chunkUsd} → <b>{numChunks}</b> чанк(ов).</span>
              {!workers.length && <span className="block text-xs text-indigo-600 mt-0.5">Точное число появится после «Проверить».</span>}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" onClick={suggest} disabled={running} title="Предложить разумные суммы и число хопов">
              ✨ Предложить
            </Button>
            <Button variant="outline" onClick={dryCheck} disabled={running}>
              <Search className="h-4 w-4 mr-1.5" /> Проверить (без денег)
            </Button>
            {!running ? (
              <Button onClick={runCycle} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Play className="h-4 w-4 mr-1.5" /> Запустить цикл
              </Button>
            ) : (
              <Button onClick={stopCycle} className="bg-red-600 hover:bg-red-700 text-white">
                <Square className="h-4 w-4 mr-1.5" /> Стоп
              </Button>
            )}
            {mode === 'linear' && !running && undelivered > 0 && runId && (
              <Button onClick={runResume} className="bg-amber-600 hover:bg-amber-700 text-white">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Resume ({undelivered})
              </Button>
            )}
            {running && <span className="inline-flex items-center text-sm text-indigo-600"><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Выполняется…</span>}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-gray-900">Лог выполнения</CardTitle>
            <CardDescription>Пошагово, в реальном времени</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[360px] overflow-y-auto rounded-lg bg-gray-900 p-3 font-mono text-xs leading-relaxed">
              {log.length === 0 ? (
                <div className="text-slate-500">Лог пуст. Нажми «Проверить» или «Запустить цикл».</div>
              ) : (
                log.map((e, i) => (
                  <div key={i} className={LEVEL_CLS[e.level] || 'text-slate-300'}>
                    <span className="text-slate-600">{e.t}</span> {e.msg}
                  </div>
                ))
              )}
              <div ref={logEndRef} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-gray-900">
              Воркеры {rows.length > 0 && <span className="text-gray-400 font-normal">· доставлено {delivered}/{rows.length}</span>}
            </CardTitle>
            <CardDescription>Суммы случайные · доставка по on-chain чекапу</CardDescription>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <div className="text-sm text-gray-400 py-8 text-center">Пусто. Нажми «Проверить (без денег)».</div>
            ) : (
              <div className="h-[360px] overflow-y-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Воркер (Tonviewer)</th>
                      <th className="text-right px-3 py-2">TON</th>
                      <th className="text-left px-3 py-2">Статус</th>
                      <th className="text-left px-3 py-2">On-chain ✓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const { Icon, cls } = stMeta(r.status);
                      const v = verify[r.address];
                      return (
                        <tr key={r.address || i} className="border-t border-gray-100">
                          <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-1.5 font-mono">
                            <a href={`https://tonviewer.com/${r.address}`} target="_blank" rel="noopener noreferrer"
                               className="text-indigo-600 hover:underline inline-flex items-center gap-1" title={r.address}>
                              {short(r.address)} <ExternalLink className="h-3 w-3" />
                            </a>
                          </td>
                          <td className="px-3 py-1.5 text-right text-gray-700">{r.amount != null ? fmt(r.amount, 2) : (plan[r.address] != null ? `~${fmt(plan[r.address], 2)}` : `${fmt(tonMin, 0)}–${fmt(tonMax, 0)}`)}</td>
                          <td className="px-3 py-1.5"><span className={`inline-flex items-center gap-1 ${cls}`}><Icon className="h-3.5 w-3.5" /> {r.status}</span></td>
                          <td className="px-3 py-1.5">
                            {v ? (v.ok
                              ? <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> +{fmt(v.delta, 2)} TON</span>
                              : <span className="text-red-600 inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" /> не дошло</span>)
                              : <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CycleBootstrap;
